import type { Dispatch, SetStateAction } from 'react';

import type { CountryAiMode, CountryGenResult } from './multiCountryBulkEdit.types';
import { generatePerDrop, type GenerationProgress } from './perDropGeneration';

export interface TextGenMutationLike {
  mutateAsync: (p: {
    groupId: string;
    countryKey: string;
    amount: number;
    translate?: boolean;
  }) => Promise<{ texts: string[]; translations?: string[] }>;
}

export async function runMultiCountryTextGeneration(params: {
  textMode: CountryAiMode;
  countryKeys: string[];
  globalCountryPerDrop: string[];
  groupId: string;
  amount: number;
  groupIdsPerDrop?: string[];
  translate?: boolean;
  genTexts: TextGenMutationLike;
  setProgress: (p: GenerationProgress | null) => void;
  setResultsByCountry: Dispatch<SetStateAction<Record<string, CountryGenResult>>>;
  onTextsPerDropMergeOrder?: (batches: string[][]) => void;
  onTextsGenerated?: (byCountry: Record<string, string[]>) => void;
}): Promise<void> {
  const {
    textMode,
    countryKeys,
    globalCountryPerDrop,
    groupId,
    amount,
    groupIdsPerDrop,
    translate,
    genTexts,
    setProgress,
    setResultsByCountry,
    onTextsPerDropMergeOrder,
    onTextsGenerated,
  } = params;
  const tr = translate === true;
  const collected: Record<string, string[]> = {};
  if (textMode === 'per_drop') {
    await generatePerDrop({
      countryKeys,
      globalCountryPerDrop,
      groupId,
      amount,
      groupIdsPerDrop,
      translate: tr,
      genFn: (gId, ck, amt, t) =>
        genTexts.mutateAsync({ groupId: gId, countryKey: ck, amount: amt, translate: t }),
      extractResult: (d) => d.texts,
      extractTranslations: (d) => d.translations,
      setProgress,
      onMergeOrderBatches: onTextsPerDropMergeOrder,
      applyResult: (ck, batches, gIds, translationBatches) => {
        const flat = batches.flat();
        collected[ck] = flat;
        setResultsByCountry((prev) => ({
          ...prev,
          [ck]: {
            ...(prev[ck] ?? { countryKey: ck, senders: [] }),
            countryKey: ck,
            texts: flat,
            textBatches: batches,
            translationBatches,
            groupIdsPerDrop: gIds,
            textSourceGroupId: groupId,
          },
        }));
      },
    });
    return;
  }
  if (textMode === 'same_all') {
    setProgress({ current: 0, total: 1, currentCountry: countryKeys[0] ?? '' });
    const data = await genTexts.mutateAsync({
      groupId,
      countryKey: countryKeys[0] ?? '',
      amount,
      translate: tr,
    });
    setResultsByCountry((prev) => {
      const next = { ...prev };
      for (const ck of countryKeys) {
        next[ck] = {
          ...(next[ck] ?? { countryKey: ck, senders: [] }),
          countryKey: ck,
          texts: data.texts,
          translations: data.translations,
          textSourceGroupId: groupId,
        };
        collected[ck] = data.texts;
      }
      return next;
    });
    setProgress({ current: 1, total: 1, currentCountry: '' });
    onTextsGenerated?.(collected);
    return;
  }
  for (let i = 0; i < countryKeys.length; i++) {
    const ck = countryKeys[i]!;
    const data = await genTexts.mutateAsync({ groupId, countryKey: ck, amount, translate: tr });
    collected[ck] = data.texts;
    setResultsByCountry((prev) => ({
      ...prev,
      [ck]: {
        ...(prev[ck] ?? { countryKey: ck, senders: [] }),
        countryKey: ck,
        texts: data.texts,
        translations: data.translations,
        textSourceGroupId: groupId,
      },
    }));
    setProgress({ current: i + 1, total: countryKeys.length, currentCountry: ck });
  }
  onTextsGenerated?.(collected);
}
